
import { EyeIcon, LinkIcon } from "@signet/ui";
import { api, NowStat, nowRangeQuery, nowRangeVersus, nowRangeWords, type NowRange, useSeenQuery } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   Visitors and Clicks on the Now page. Both cards read one request (the same
   query key), so the page asks Links once however many of its cards it shows.
   ────────────────────────────────────────────────────────────────────────── */

type Figure = { value: number; previous: number; spark: number[] };
type Stats = { from: string; to: string; visitors: Figure; clicks: Figure };

function useLinksNow(range: NowRange) {
  return useSeenQuery({
    queryKey: ["links", "now-stats", nowRangeQuery(range)],
    queryFn: () => api<Stats>(`/api/m/links/now-stats?${nowRangeQuery(range)}`),
    refetchInterval: 60_000,
  });
}

function LinksStat({ range, which }: { range: NowRange; which: "visitors" | "clicks" }) {
  const stats = useLinksNow(range);
  const figure = stats.data?.[which];
  const visitors = which === "visitors";
  return (
    <NowStat
      icon={visitors ? <EyeIcon size={18} /> : <LinkIcon size={18} />}
      label={`${visitors ? "Visitors" : "Clicks"} ${nowRangeWords(range)}`}
      colour={visitors ? "var(--steel)" : "var(--accent)"}
      value={figure ? figure.value.toLocaleString() : null}
      current={figure?.value}
      previous={figure?.previous}
      versus={nowRangeVersus(range)}
      spark={figure?.spark}
      failed={stats.isError}
      // Analytics over the same days the card counted (Jay, 2026-10-13).
      href="/links/analytics"
      search={stats.data ? { from: stats.data.from, to: stats.data.to } : undefined}
    />
  );
}

export const VisitorsNow = ({ range }: { range: NowRange }) => <LinksStat range={range} which="visitors" />;
export const ClicksNow = ({ range }: { range: NowRange }) => <LinksStat range={range} which="clicks" />;
